<script setup>
import { api, DmDropdownMenuItem, DmTag, FullScreenPopup, Icon, OrgEntity, url } from 'dmui_components'
import { ref } from 'vue'
import AnnotationHighlighter from '../nlp_check/AnnotationHighlighter.vue'
import AnnotationBySentenceFormatter from './AnnotationBySentenceFormatter'
import getSafeIconName from '@/utility/safeIconName'
import useReportTypeMgmt from '../../document/report/composables/useReportTypeMgmt'

const { getMappedDocumentType } = useReportTypeMgmt()

const props = defineProps({
	term: { type: Object, default: () => ({}) },
	report: {
		type: Object,
		default: () => ({})
	}
})
const mappedDocumentType = getMappedDocumentType(props.report.document_type)

const showReviewAnnotations = ref(false)

const sentences = ref({})

const total = ref()

const highlightTerm = [{ termIds: [props.term?.term_id], term_text: props.term?.term_text }]

async function loadSentence() {
	let response = []
	if (!props.term?.term_id && !props.report.report_id) {
		return
	}
	if (props.term?.term_id) {
		response = await api(url`/nlp_check/${props.term.term_id}`)
		total.value = response.total
	} else {
		response = await api(url`/nlp_check/report/${props.report.document_id}`)
	}
	sentences.value = response.sentences
}

async function onShowSentence() {
	showReviewAnnotations.value = true
	loadSentence()
}

function getAnnotationBySentence(sentence) {
	let ListAnnotation = [...sentence.primary_annotations, ...sentence.secondary_annotations].sort((a, b) => a.segment_start - b.segment_start)

	return new AnnotationBySentenceFormatter(ListAnnotation).getAnnotationBySentence(sentence)
}
</script>
<template>
	<DmDropdownMenuItem id="show-sentence-btn" @click="onShowSentence">
		<Icon name="uil-tag-alt" size="midi" />
		<span>Review Annotations</span>
	</DmDropdownMenuItem>
	<FullScreenPopup :show="showReviewAnnotations" @update:show="showReviewAnnotations = false">
		<template #breadcrumb>
			<slot name="title" />
		</template>
		<div class="content">
			<div v-if="term?.term_id" class="number-total">
				<p class="total primary-text">
					Showing <span class="primary-text bold">{{ sentences.length }} out of {{ total }} sentences</span> mentioning the term
				</p>
				<AnnotationHighlighter :text="term.term_text" :highlights="highlightTerm" :term_id="term.term_id" />
			</div>
			<div v-if="report?.report_id" class="popup-header">
				<OrgEntity size="xhuge"></OrgEntity>
				<div class="report-header">
					<div class="report-details">
						<p class="secondary-header">{{ report.org_name }}</p>
						<p>{{ mappedDocumentType }} Report {{ report.document_year }}</p>
					</div>
					<div class="report-details">
						<DmTag class="neutral">
							<Icon :name="getSafeIconName('ic_flag', report.country_name)" size="mini" />
							<div class="tag-text" :title="report.country_name">{{ report.country_name }} - {{ report.region_name }}</div>
						</DmTag>
						<DmTag class="neutral">
							<Icon :name="getSafeIconName('ic', report.industry_name)" size="mini" />
							<div class="tag-text" :title="report.industry_name">{{ report.industry_name }}</div>
						</DmTag>
					</div>
				</div>
			</div>
			<div class="sentence-container">
				<template v-for="sentence in sentences" :key="`sentence-${sentence.sentence_id}`">
					<div :id="sentence.sentence_id" class="sentence">
						<AnnotationHighlighter :text="sentence.sentence_text" :highlights="getAnnotationBySentence(sentence)" :term_id="term?.term_id" />
					</div>
				</template>
			</div>
		</div>
	</FullScreenPopup>
</template>

<style lang="less" scoped>
.apply-shadow() {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	box-shadow: 0px 1px 4px 0px fade(@gray-100, 20%);
	border-radius: inherit;
}

.sentence {
	border: 2px solid transparent;
	box-shadow: none;
	position: relative;
	padding: 16px;
	border-radius: 8px;
	margin: 4px 30px;
	&::before {
		.apply-shadow();
	}
}

.sencondary-header {
	color: @gray-100;
}

.total {
	color: @gray-100;
}
.number-total {
	display: flex;
	padding: 16px 0px;
	margin: 4px 30px;
	gap: 0px 4px;
}

.content {
	height: 100%;
	overflow: scroll;
}
.popup-header {
	display: flex;
	gap: @micro;
	padding: 20px;
	align-items: center;
}
.report-details {
	display: flex;
	gap: @micro;
}
.report-header {
	display: flex;
	flex-direction: column;
	gap: @micro;
	color: @gray-100;
}
</style>
